import { useEffect, useState, useCallback } from "react";
import StaffShell from "../components/StaffShell";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Music, ThumbsUp, Check, X, Play, ChevronDown, ChevronUp } from "lucide-react";
import { VipBadge } from "../components/VipBadge";
import { T, useI18n } from "../lib/i18n";

const NORMALIZE = { pending: "requested", approved: "queued", playing: "queued" };
const norm = (s) => {
  const l = (s || "").toLowerCase();
  return NORMALIZE[l] || l;
};

export default function DJDash() {
  useI18n();
  const { eventId } = useMotif();
  const [music, setMusic] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/music");
      setMusic(data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "music_new" || msg.type === "music_update" || msg.type === "ws_reconnected") refresh();
  });

  const setStatus = async (id, status, verb) => {
    // Optimistic
    setMusic((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
    try {
      await api.put(`/music/${id}/status`, { status });
      toast.success(`${T(verb)} · ${T("saved")}`);
    } catch (e) { toast.error(formatErr(e)); refresh(); }
  };

  // Sort: VIP first, then votes desc, then newest first
  const sortFn = (a, b) => {
    const av = a.vip ? 0 : 1;
    const bv = b.vip ? 0 : 1;
    if (av !== bv) return av - bv;
    if ((b.vote_count || 0) !== (a.vote_count || 0)) return (b.vote_count || 0) - (a.vote_count || 0);
    return (b.created_at || "").localeCompare(a.created_at || "");
  };

  const sorted = [...music].sort(sortFn);
  const requested = sorted.filter((m) => norm(m.status) === "requested");
  const queued = sorted.filter((m) => norm(m.status) === "queued");
  const history = sorted.filter((m) => ["played", "rejected"].includes(norm(m.status)));

  return (
    <StaffShell title={T("DJ booth")} subtitle={`${T("Requested")} → ${T("Queued")}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Column title={T("Requested")} icon={Music} items={requested} testid="dj-col-requested">
          {(m) => (
            <>
              <button onClick={() => setStatus(m.id, "queued", "Queued")} className="btn-primary text-[11px] px-2 py-1 flex items-center gap-1" data-testid={`dj-queue-${m.id}`}>
                <Check className="w-3 h-3" /> {T("Add to Queue")}
              </button>
              <button onClick={() => setStatus(m.id, "rejected", "Rejected")} className="btn-ghost text-[11px] px-2 py-1" data-testid={`dj-reject-${m.id}`} aria-label="Reject">
                <X className="w-3 h-3" />
              </button>
            </>
          )}
        </Column>
        <Column title={T("Queued")} icon={Play} items={queued} testid="dj-col-queued">
          {(m) => (
            <>
              <button onClick={() => setStatus(m.id, "played", "Marked played")} className="btn-primary text-[11px] px-2 py-1 flex items-center gap-1" data-testid={`dj-played-${m.id}`}>
                <Play className="w-3 h-3" /> {T("Mark Played")}
              </button>
              <button onClick={() => setStatus(m.id, "requested", "Removed")} className="btn-ghost text-[11px] px-2 py-1" data-testid={`dj-remove-${m.id}`} aria-label="Remove from queue">
                <X className="w-3 h-3" />
              </button>
            </>
          )}
        </Column>
      </div>

      <div className="mt-6 card-elev p-3">
        <button
          onClick={() => setHistoryOpen((o) => !o)}
          className="flex items-center gap-2 text-xs tracking-luxe text-motif-muted w-full"
          data-testid="dj-history-toggle"
        >
          {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {T("History")} ({history.length})
        </button>
        {historyOpen && (
          <div className="space-y-1 mt-3 max-h-64 overflow-y-auto">
            {history.length === 0 && <div className="text-[11px] text-motif-muted italic">{T("No history yet")}</div>}
            {history.map((m) => (
              <div key={m.id} className="p-2 rounded-lg bg-accent-motif flex items-center justify-between" data-testid={`dj-history-${m.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <VipBadge show={!!m.vip} />
                    <div className="font-display text-xs text-motif truncate">{m.title}</div>
                  </div>
                  <div className="text-[10px] text-motif-muted truncate">{m.artist || "—"} · {T(norm(m.status))}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </StaffShell>
  );
}

function Column({ title, icon: Icon, items, testid, children }) {
  return (
    <div className="card-elev p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-primary-motif" />
        <div className="font-display text-lg">{title}</div>
        <span className="ml-auto text-[10px] tracking-luxe text-motif-muted">{items.length}</span>
      </div>
      <div className="space-y-2 max-h-[65vh] overflow-y-auto">
        {items.length === 0 && <div className="text-[11px] text-motif-muted italic text-center py-3">{T("Empty")}</div>}
        {items.map((m) => (
          <div key={m.id} className="p-3 rounded-xl bg-accent-motif" data-testid={`dj-row-${m.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <VipBadge show={!!m.vip} />
                  <div className="font-display text-base text-motif truncate">{m.title}</div>
                </div>
                <div className="text-[10px] text-motif-muted truncate">
                  {m.artist || "—"} · <span className="inline-flex items-center gap-0.5"><ThumbsUp className="w-3 h-3" />{m.vote_count || 0}</span>
                  {m.guest_name && <> · {m.guest_name}</>}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {children(m)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
